import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { IDonorShieldHistoryReportResponse } from '@nkr/store/index';
import { ICenter, IDonorShieldDirect, IOrgan } from '@nkr/models/index';
import { GridWrapper, HeaderWrapper } from './styles';
import { AgGridReact, AgGridColumnProps, AgGridColumnGroupProps } from 'ag-grid-react';
import {
    IHeaderParams, GridApi, ColumnApi, GridReadyEvent,
    CsvExportParams, ValueGetterParams, ProcessCellForExportParams, FirstDataRenderedEvent, ICellRendererParams
} from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { FormControl, Button } from 'react-bootstrap';

type MyColumn = AgGridColumnProps | AgGridColumnGroupProps;

interface IProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getDonorShieldReport: (startDate: string, endDate: string, centerId: number, organType: number) => Promise<IDonorShieldHistoryReportResponse>
}

interface IState {
    startDate: string,
    endDate: string,
    centerId: number,
    organType: number,
    report: IDonorShieldDirect[],
    centers: ICenter[],
    organs: IOrgan[]
}

class DonorShieldReportGrid extends React.Component<IProps, {}> {
    readonly state: IState = {
        startDate: '',
        endDate: '',
        centerId: 0,
        organType: -1,
        report: [],
        centers: [],
        organs: []
    }

    gridApi: GridApi = null;
    gridColumnApi: ColumnApi = null;

    onGridReady = (event: GridReadyEvent) => {
        this.gridApi = event.api;
        this.gridColumnApi = event.columnApi;

        event.api.sizeColumnsToFit();
    }

    onFirstDataRendered = (event: FirstDataRenderedEvent) => {
        event.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        //get dates
        let startDate = '';
        let endDate = '';
        let centerId = 0;
        let organType = -1;

        if (startDate == '' || endDate == '') {
            startDate = moment().startOf('month').format('YYYY-MM-DD');
            endDate = moment().endOf('month').format('YYYY-MM-DD');
        } else {
            startDate = this.state.startDate;
            endDate = this.state.endDate;
        }

        this.getData(startDate, endDate, centerId, organType);
    }

    getData = (startDate: string, endDate: string, centerId: number, organType: number) => {
        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getDonorShieldReport(startDate, endDate, centerId, organType)
                .then((response: IDonorShieldHistoryReportResponse) => {
                    this.setState({
                        startDate: startDate,
                        endDate: endDate,
                        centerId: centerId,
                        report: response.data.results,
                        centers: response.data.centers,
                        organs: response.data.organs
                    });

                    this.props.hideLoading();
                }).catch((ex) => {
                    alert('An error occured while loading report data');
                    this.props.hideLoading();
                });
        });
    }

    dateRangeSearch = () => {
        this.getData(this.state.startDate, this.state.endDate, this.state.centerId, this.state.organType);
    }

    onGridSizeChanged = () => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = this.gridColumnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        this.gridColumnApi.setColumnsVisible(columnsToShow, true);
        this.gridColumnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        this.gridApi.sizeColumnsToFit();
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        this.setState({
            [name]: value
        });
    }
  
    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let selectedCenterId = parseInt(e.currentTarget.value);

        this.setState({
            centerId: selectedCenterId
        });
    }

    handleOrganChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let selectedOrganId = parseInt(e.currentTarget.value);

        this.setState({
            organType: selectedOrganId
        });
    }

    csvExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'Donor-Shield-Direct-History.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.headerName == 'Rank')
                    return params.node.rowIndex + 1;
                else if (colDef.colId == 'xpDate')
                    return moment(params.value).format('MM/DD/YYYY');

                return params.value;
            }
        };

        this.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        const centerList = _.sortBy(_.uniqBy(this.state.centers.map(x => ({ id: x.id, alias: x.alias })), item => item.id), item => item.alias);

        const reportColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Rank
                    </SortableHeaderComponent>,
                headerName: 'Rank',
                width: 55,
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
            },
            {
                colId: 'organType',
                field: 'organType',
                headerName: 'Organ',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    < SortableHeaderComponent {...props} >
                        Organ
                    </SortableHeaderComponent >,

                minWidth: 130,
                maxWidth: 150,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'centerAlias',
                field: 'center.alias',
                headerName: 'Center',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                < SortableHeaderComponent { ...props } >
                        Center
                    </SortableHeaderComponent >,
    
                minWidth: 130,
                maxWidth: 150,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'surgeryDate',
                field: 'surgeryDate',
                headerName: 'TXP Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        TXP Date
                    </SortableHeaderComponent>,
                cellRendererFramework: (props: ICellRendererParams) => {
                    var surgeryDate = props.data.surgeryDate;

                    return (
                        moment(surgeryDate).format('MM/DD/YYYY')
                    )
                },
                minWidth: 130,
                maxWidth: 150,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        D Alias
                    </SortableHeaderComponent>,
                minWidth: 130,
                maxWidth: 150,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorUnosId',
                field: 'donorUnosId',
                headerName: 'D UNOS Id',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        D UNOS ID
                    </SortableHeaderComponent>,
                minWidth: 130,
                maxWidth: 150,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAboType',
                field: 'donorAboType',
                headerName: 'D ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                minWidth: 130,
                maxWidth: 150,
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        D ABO
                    </SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorGender',
                field: 'donorGender',
                headerName: 'D Gender',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        D Gender
                    </SortableHeaderComponent>,
                sortable: true,
                minWidth: 130,
                maxWidth: 150,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAge',
                field: 'donorAge',
                headerName: 'D Age ATOT',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        D Age ATOT
                    </SortableHeaderComponent>,
               
                minWidth: 130,
                maxWidth: 150,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
          
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        R Alias
                    </SortableHeaderComponent>,
                minWidth: 130,
                maxWidth: 150,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientAboType',
                field: 'recipientAboType',
                headerName: 'R ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        R ABO
                    </SortableHeaderComponent>,
                // minWidth: 200,
                // maxWidth: 220,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientGender',
                field: 'recipientGender',
                headerName: 'R Gender',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        R Gender
                    </SortableHeaderComponent>,
                // minWidth: 180,
                // maxWidth: 200,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientAge',
                field: 'recipientAge',
                headerName: 'R Age ATOT',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        R Age ATOT
                    </SortableHeaderComponent>,
                // minWidth: 180,
                // maxWidth: 200,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
        ];

        return (
            <>
                <HeaderWrapper>
                    <div className='billing-header'>
                        Donor Shield Direct History Report
                    </div>

                    <div className='well well-sm' style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                <div style={{ marginRight: 5 }}>TXP Start Date:&nbsp;</div>
                                <div>
                                    <FormControl
                                        type='date'
                                        name='startDate'
                                        value={this.state.startDate}
                                        onChange={this.handleDateChange}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                <div style={{ marginRight: 5 }}>TXP End Date:</div>
                                <div>
                                    <FormControl
                                        type='date'
                                        name='endDate'
                                        value={this.state.endDate}
                                        onChange={this.handleDateChange}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "flex", alignItems: "center" }}>
                                <div style={{ marginRight: 5 }}>Filter by Organ:</div>
                                <div style={{ marginRight: 20 }}>
                                    <FormControl
                                        componentClass='select'
                                        value={this.state.organType}
                                        onChange={this.handleOrganChange}>
                                        <option value={-1}>- All Organs -</option>
                                        {this.state.organs.map(organ =>
                                            <option key={`ctr-${organ.id}`} value={organ.id}>{organ.name}</option>
                                        )}
                                    </FormControl>
                                </div>
                            </div>

                            <div style={{ display: "flex", alignItems: "center" }}>
                                <div style={{ marginRight: 5 }}>Filter by Center:</div>
                                <div style={{ marginRight: 20 }}>
                                    <FormControl
                                        componentClass='select'
                                        value={this.state.centerId}
                                        onChange={this.handleCenterChange}>
                                        <option value={0}>- All Centers -</option>
                                        {centerList.map(center =>
                                            <option key={`ctr-${center.id}`} value={center.id}>{center.alias}</option>
                                        )}
                                    </FormControl>
                                </div>
                            </div>

                            <div>
                                <Button bsStyle='primary' onClick={this.dateRangeSearch}>Search</Button>
                            </div>
                        </div>

                        <div>
                            <Button onClick={this.csvExport}>Export CSV</Button>
                        </div>
                    </div>
                </HeaderWrapper>

                <div className='row'>
                    <div className='col-sm-12'>
                        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 20 }}>
                            <AgGridReact
                                enableCellTextSelection={true}
                                enableRangeSelection={true}
                                columnDefs={reportColumns}
                                rowData={this.state.report}
                                headerHeight={30}
                                noRowsOverlayComponentFramework={() =>
                                    <div>No data for the selected date range...</div>}
                                onGridReady={this.onGridReady}
                                onFirstDataRendered={this.onFirstDataRendered}
                                onGridSizeChanged={this.onGridSizeChanged}
                                suppressCellSelection={true}
                                // rowBuffer={10000}
                                onSortChanged={() => {
                                    this.gridApi.refreshCells();
                                }}
                            />
                        </GridWrapper>
                    </div>
                </div>
            </>
        );
    }
}

export default DonorShieldReportGrid;